import React from 'react';

import type { PlotlyTypes } from 'visyn_core/plotly';
import { PlotlyComponent } from 'visyn_core/plotly';

import { getDataForFamilyGraph, getTitleForFamilyGraph } from './repertoire-analysis-utils';
import type { ImmuneGeneRawData } from '../generated/generatedOpenAPI';
import { DisplayType, FamilyOptions } from '../types/experiment';

interface VGenesProps {
  selectedFamilyOption: FamilyOptions;
  geneDistribution: ImmuneGeneRawData | undefined;
  selectedDisplayType: DisplayType;
}

export default function VGenes({ selectedFamilyOption, geneDistribution, selectedDisplayType }: VGenesProps) {
  const data = React.useMemo(() => {
    const title = getTitleForFamilyGraph(selectedFamilyOption);
    if (!geneDistribution) {
      return {
        title,
        data: [],
        xValues: [] as string[],
      };
    }
    const innerData = getDataForFamilyGraph(
      geneDistribution,
      selectedFamilyOption,
      { VH: '#337ab7', VK: '#f75a1e', VL: '#15a154' },
      selectedDisplayType === DisplayType.PERCENTAGE,
    );
    return {
      title,
      data: innerData,
      xValues: (innerData[0]?.x ?? []) as string[],
    };
  }, [geneDistribution, selectedFamilyOption, selectedDisplayType]);

  const yAxisLabel: string = selectedDisplayType === DisplayType.PERCENTAGE ? 'Percentage of cells' : 'Cells';

  return (
    <div data-testid="v-genes">
      <PlotlyComponent
        data={data.data as PlotlyTypes.Data[]}
        useResizeHandler
        style={{ width: '100%' }}
        layout={{
          height: 500,
          margin: {
            l: 80,
            r: 40,
            b: 100,
            t: 30,
          },
          yaxis: {
            title: {
              text: yAxisLabel,
              font: { size: 16 },
            },
          },
          xaxis: {
            type: 'category',
            title: {
              text: 'V genes',
              font: { size: 16 },
            },
            tickmode: 'array',
            tickvals: data.xValues,
            automargin: true,
          },
          title: {
            text: data.title,
            font: { size: 16 },
          },
        }}
        config={{
          displaylogo: false,
          modeBarButtonsToRemove: ['pan2d', 'lasso2d', 'autoScale2d', 'select2d'],
        }}
      />
    </div>
  );
}
